function authorLabel(note, membersById, currentUser) {
  if (!note || !note.author_user_id) {
    return "Unknown author";
  }
  if (currentUser && note.author_user_id === currentUser.user_id) {
    return currentUser.full_name || currentUser.email || "You";
  }
  const member = membersById.get(note.author_user_id);
  if (member) {
    return member.full_name || member.email || note.author_user_id;
  }
  return note.author_user_id;
}

function bodyPreview(value) {
  const text = String(value || "").trim();
  if (!text) {
    return "No note body saved.";
  }
  return text.length > 120 ? `${text.slice(0, 117)}...` : text;
}

function buildMemberDirectory(members, currentUser) {
  const map = new Map();
  for (const item of [...(Array.isArray(members) ? members : []), currentUser].filter(Boolean)) {
    if (!item.user_id || map.has(item.user_id)) {
      continue;
    }
    map.set(item.user_id, item);
  }
  return map;
}

function noteTone(note) {
  if (!note) {
    return "muted";
  }
  if (note.note_type === "manager_review") {
    return "ready";
  }
  if (note.note_type === "qualification") {
    return "ready";
  }
  if (note.note_type === "handoff") {
    return "stale";
  }
  if (note.note_type === "manager_guidance") {
    return "overdue";
  }
  return "ready";
}

function noteLabel(note) {
  if (!note) {
    return "Internal note";
  }
  if (note.note_type === "qualification") {
    return "Lead qualification";
  }
  if (note.note_type === "manager_review") {
    return "Manager review";
  }
  if (note.note_type === "manager_guidance") {
    return "Manager guidance";
  }
  return bodyPreview(note.body);
}

export function buildInternalNotesWorkspace(options = {}) {
  const settings = Object.assign(
    {
      notes: [],
      members: [],
      currentUser: null,
      conversation: null,
      available: true,
      unavailableReason: null,
    },
    options
  );
  const notes = Array.isArray(settings.notes) ? settings.notes : [];
  const currentUser = settings.currentUser || null;
  const conversation = settings.conversation || {};
  const membersById = buildMemberDirectory(settings.members, currentUser);
  const latestNote = notes[0] || null;
  const latestHandoff = notes.find((item) => item.note_type === "handoff") || null;

  const unavailableMessage =
    settings.unavailableReason || "Internal notes become available once this conversation is linked to a lead record.";

  return {
    summaryCards: [
      {
        label: "Internal notes",
        value: String(notes.length),
        detail: notes.length ? "Team context saved on the CRM record." : unavailableMessage,
        tone: settings.available ? (notes.length ? "ready" : "muted") : "stale",
      },
      {
        label: "Latest author",
        value: latestNote ? authorLabel(latestNote, membersById, currentUser) : "No notes yet",
        detail: latestNote ? latestNote.created_at || "Timestamp unavailable" : "No internal collaboration saved.",
        tone: latestNote ? noteTone(latestNote) : "muted",
      },
      {
        label: "Handoff posture",
        value: latestHandoff ? "Guidance saved" : conversation.assigned_user_id ? "Assigned" : "Unassigned",
        detail: latestHandoff
          ? bodyPreview(latestHandoff.body)
          : conversation.assigned_user_id
            ? "Conversation already has an owner, but no explicit handoff note."
            : "Add a handoff note when reassigning or releasing record context.",
        tone: latestHandoff ? "stale" : conversation.assigned_user_id ? "ready" : "muted",
      },
    ],
    noteRows: settings.available
      ? notes.length
        ? notes.slice(0, 6).map((item) => ({
            id: item.id,
            label: noteLabel(item),
            value: item.note_type || "internal",
            tone: noteTone(item),
            meta: `${authorLabel(item, membersById, currentUser)} | ${["qualification", "manager_review", "manager_guidance"].includes(item.note_type) ? bodyPreview(item.body) : item.created_at || "Timestamp unavailable"}`,
          }))
        : [
            {
              label: "No internal notes yet",
              value: "empty",
              tone: "muted",
              meta: "Use internal notes for handoff context, manager guidance, and record collaboration.",
            },
          ]
      : [
          {
            label: "Internal notes unavailable",
            value: "locked",
            tone: "stale",
            meta: unavailableMessage,
          },
        ],
    guidanceRows: [
      latestHandoff
        ? {
            label: "Latest handoff guidance",
            value: latestHandoff.note_type,
            tone: "stale",
            meta: `${authorLabel(latestHandoff, membersById, currentUser)} | ${bodyPreview(latestHandoff.body)}`,
          }
        : {
            label: "Latest handoff guidance",
            value: "none",
            tone: settings.available ? "muted" : "stale",
            meta: settings.available
              ? "No handoff note has been saved for this record yet."
              : unavailableMessage,
          },
      latestNote
        ? {
            label: "Collaboration anchor",
            value: latestNote.note_type || "internal",
            tone: noteTone(latestNote),
            meta: `${authorLabel(latestNote, membersById, currentUser)} | ${bodyPreview(latestNote.body)}`,
          }
        : {
            label: "Collaboration anchor",
            value: "no note",
            tone: settings.available ? "muted" : "stale",
            meta: settings.available
              ? "Create an internal note to keep team context inside LandSaaS."
              : unavailableMessage,
          },
      {
        label: "Assignment context",
        value: conversation.assigned_user_id ? "owned" : "open handoff",
        tone: conversation.assigned_user_id ? "ready" : "stale",
        meta: conversation.assigned_user_id
          ? "This record currently has an assigned owner."
          : "Use a handoff note before moving the record between teammates.",
      },
    ],
    canCreate: Boolean(settings.available),
    unavailableReason: unavailableMessage,
  };
}
